import clone from "lodash/clone";
import mergeWith from "lodash/mergeWith";
import { computed, readonly, ref, shallowRef, triggerRef } from "vue";
import menuData from "@/config/menu.json";
import { getChildCategories, getMenu } from "@/core/api/graphql";
import { useCurrency } from "@/core/composables/useCurrency";
import { useModuleSettings } from "@/core/composables/useModuleSettings";
import { useThemeContext } from "@/core/composables/useThemeContext";
import { MODULE_XAPI_KEYS } from "@/core/constants/modules";
import {
  convertToExtendedMenuLink,
  getFilterExpressionForCategorySubtree,
  getFilterExpressionForInStock,
  getFilterExpressionForZeroPrice,
  Logger,
  categoryToExtendedMenuLink,
  getTranslatedMenuLink,
} from "@/core/utilities";
import { globals } from "../globals";
import type { ExtendedMenuLinkType, MenuType } from "../types";
import type { DeepPartial } from "utility-types";

const { currentCurrency } = useCurrency();

const loading = ref(false);
const matchingRouteName = ref("");
const menuSchema = shallowRef<MenuType | null>(null);
const catalogMenuItems = shallowRef<ExtendedMenuLinkType[]>([]);
const footerLinks = shallowRef<ExtendedMenuLinkType[]>([]);
const footerListLeft = shallowRef<ExtendedMenuLinkType[]>([]);
const footerListCenterLeft = shallowRef<ExtendedMenuLinkType[]>([]);
const footerListCenterRight = shallowRef<ExtendedMenuLinkType[]>([]);
const footerListRight = shallowRef<ExtendedMenuLinkType[]>([]);
const footerListBottom = shallowRef<ExtendedMenuLinkType[]>([]);

const desktopMainMenuItems = computed<ExtendedMenuLinkType[]>(() =>
  (menuSchema.value?.header?.desktop?.main || [])
    .map((item: ExtendedMenuLinkType) => getTranslatedMenuLink(item))
    .sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0)),
);

const desktopAccountMenuItems = computed<ExtendedMenuLinkType | undefined>(() => {
  const schema = menuSchema.value?.header?.desktop?.account
    ? clone(getTranslatedMenuLink(menuSchema.value.header.desktop.account))
    : undefined;
  if (Array.isArray(schema?.children)) {
    schema.children.sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0));
  }
  return schema;
});

const desktopCorporateMenuItems = computed<ExtendedMenuLinkType | undefined>(() => {
  return menuSchema.value ? getTranslatedMenuLink(menuSchema.value?.header?.desktop?.corporate) : undefined;
});

const mobileMainMenuItems = computed<ExtendedMenuLinkType[]>(() =>
  (menuSchema.value?.header?.mobile?.main || []).map((item: ExtendedMenuLinkType) => {
    const menuLink: ExtendedMenuLinkType = getTranslatedMenuLink(item);

    if (menuLink.id === "catalog") {
      menuLink.children = catalogMenuItems.value;
    }

    return menuLink;
  }),
);

const mobileCatalogMenuItem = computed<ExtendedMenuLinkType | undefined>(
  () => mobileMainMenuItems.value.find((item) => item.id === "catalog") || undefined,
);

const mobileAccountMenuItem = computed<ExtendedMenuLinkType | undefined>(() => {
  if (!menuSchema.value) {
    return undefined;
  }

  return getTranslatedMenuLink(menuSchema.value?.header?.mobile?.account);
});

const mobileCorporateMenuItem = computed<ExtendedMenuLinkType | undefined>(() =>
  menuSchema.value ? getTranslatedMenuLink(menuSchema.value.header.mobile.corporate) : undefined,
);

const mobilePreSelectedMenuItem = computed<ExtendedMenuLinkType | undefined>(() => {
  const matchedRouteNames = globals.router.currentRoute.value.matched
    .map((item) => item.name)
    .concat(matchingRouteName.value)
    .filter(Boolean);

  let preSelectedLink: ExtendedMenuLinkType | undefined;

  if (["Catalog", "Category", "Product"].some((item) => matchedRouteNames.includes(item))) {
    preSelectedLink = mobileCatalogMenuItem.value;
  } else if (matchedRouteNames.includes("Account") && !matchedRouteNames.includes("Dashboard")) {
    preSelectedLink = mobileAccountMenuItem.value;
  } else if (matchedRouteNames.includes("Company")) {
    preSelectedLink = mobileCorporateMenuItem.value;
  }

  return preSelectedLink;
});

export function useNavigations() {
  const { themeContext } = useThemeContext();
  const { getSettingValue } = useModuleSettings(MODULE_XAPI_KEYS.MODULE_ID);

  async function fetchCatalogMenu() {
    const { zero_price_product_enabled } = themeContext.value.settings;

    const catalog_menu_link_list_name = getSettingValue(MODULE_XAPI_KEYS.CATALOG_MENU_LINK_LIST_NAME);
    const catalog_empty_categories_enabled = getSettingValue(MODULE_XAPI_KEYS.CATALOG_EMPTY_CATEGORIES_ENABLED);

    try {
      if (catalog_menu_link_list_name && typeof catalog_menu_link_list_name === "string") {
        // Use a list of links
        catalogMenuItems.value = (await getMenu(catalog_menu_link_list_name)).map((item) =>
          convertToExtendedMenuLink(item, true),
        );
      } else {
        // Use the query `childCategories`, with `maxLevel` equal to 2
        const catalogId = themeContext.value.catalogId;
        const currencyCode = currentCurrency.value.code;

        const productFilter = catalog_empty_categories_enabled
          ? undefined
          : [
              getFilterExpressionForCategorySubtree({ catalogId }),
              getFilterExpressionForZeroPrice(!!zero_price_product_enabled, currencyCode),
              getFilterExpressionForInStock(true),
            ]
              .filter(Boolean)
              .join(" ");

        // Innovadis custom
        catalogMenuItems.value = (
          await getChildCategories({
            maxLevel: 2,
            onlyActive: true,
            productFilter,
          })
        )
          .map((item) => categoryToExtendedMenuLink(item, true))
          .sort((a, b) => {
            if (a.priority === undefined) {
              return 1;
            }
            if (b.priority === undefined) {
              return -1;
            }
            return a.priority - b.priority;
          });
      }
    } catch (e) {
      Logger.error(`${useNavigations.name}.${fetchCatalogMenu.name}`, e);
    }
  }

  // Innovadis custom
  async function fetchFooterLinks() {
    try {
      footerLinks.value = (await getMenu("footer-links")).map((item) => convertToExtendedMenuLink(item, false));
      footerListLeft.value = (await getMenu("footer-menu-1")).map((item) => convertToExtendedMenuLink(item, false));
      footerListCenterLeft.value = (await getMenu("footer-menu-2")).map((item) =>
        convertToExtendedMenuLink(item, false),
      );
      footerListCenterRight.value = (await getMenu("footer-menu-3")).map((item) =>
        convertToExtendedMenuLink(item, false),
      );
      footerListRight.value = (await getMenu("footer-menu-4")).map((item) => convertToExtendedMenuLink(item, false));
      footerListBottom.value = (await getMenu("footer-menu-bottom")).map((item) =>
        convertToExtendedMenuLink(item, false),
      );
    } catch (e) {
      Logger.error(`${useNavigations.name}.${fetchFooterLinks.name}`, e);
    }
  }

  async function fetchMenus() {
    loading.value = true;
    menuSchema.value = menuData as MenuType;
    await Promise.all([fetchCatalogMenu(), fetchFooterLinks(), fetchFooterLinks()]);
    loading.value = false;
  }

  function setMatchingRouteName(value: string) {
    matchingRouteName.value = value;
  }

  function mergeMenuSchema(additionalSchema: DeepPartial<MenuType>) {
    menuSchema.value = mergeWith(menuSchema.value, additionalSchema, (objValue: unknown, srcValue: unknown) => {
      if (Array.isArray(objValue) && Array.isArray(srcValue)) {
        return objValue.concat(srcValue) as ExtendedMenuLinkType[];
      }
    });
    triggerRef(menuSchema);
  }

  return {
    fetchMenus,
    setMatchingRouteName,
    desktopMainMenuItems,
    desktopAccountMenuItems,
    desktopCorporateMenuItems,
    mobileMainMenuItems,
    mobileCatalogMenuItem,
    mobileAccountMenuItem,
    mobileCorporateMenuItem,
    mobilePreSelectedMenuItem,
    matchingRouteName: readonly(matchingRouteName),
    catalogMenuItems: computed(() => catalogMenuItems.value),
    footerLinks: computed(() => footerLinks.value),

    mergeMenuSchema,

    // Innovadis custom
    footerListLeft: computed(() => footerListLeft.value),
    footerListCenterLeft: computed(() => footerListCenterLeft.value),
    footerListCenterRight: computed(() => footerListCenterRight.value),
    footerListRight: computed(() => footerListRight.value),
    footerListBottom: computed(() => footerListBottom.value),
  };
}
